import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom hook for Unreal Engine Pixel Streaming
 * Handles connection, video streaming, and command sending
 * 
 * @param {string} streamUrl - The base URL of the Unreal Pixel Streaming server
 * @returns {Object} - Pixel streaming state and methods
 */
export const usePixelStreaming = (streamUrl) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [player, setPlayer] = useState(null);
  const videoElementRef = useRef(null);
  const pixelStreamingRef = useRef(null);
  const libraryLoadedRef = useRef(false);
  const wsConnectionRef = useRef(null);
  const commandTimingsRef = useRef(new Map());

  /**
   * Load the Pixel Streaming library from the Unreal server
   */
  const loadPixelStreamingLibrary = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (libraryLoadedRef.current) {
        resolve();
        return;
      }

      // Check if library is already loaded
      if (window.PixelStreaming || window.Application) {
        libraryLoadedRef.current = true;
        resolve();
        return;
      }

      // Try loading from common Pixel Streaming paths
      // Handle both base URLs and full player.html URLs
      const script = document.createElement('script');
      
      // Extract base URL if we have a full player.html URL
      let baseUrl = streamUrl;
      if (baseUrl.includes('/player.html')) {
        // Remove query parameters first
        if (baseUrl.includes('?')) {
          baseUrl = baseUrl.split('?')[0];
        }
        // Remove /player.html to get base path
        baseUrl = baseUrl.replace('/player.html', '');
      }
      // Remove trailing slash
      if (baseUrl.endsWith('/')) {
        baseUrl = baseUrl.slice(0, -1);
      }
      
      // Try multiple possible library paths
      const possiblePaths = [
        `${baseUrl}/scripts/pixelstreaming.js`,
        `${baseUrl}/scripts/ps.js`,
        `${baseUrl}/scripts/app.js`,
        `${baseUrl}/pixelstreaming.js`,
        // Also try loading from the player.html page itself (library might be embedded)
        streamUrl.includes('/player.html') ? streamUrl : null
      ].filter(Boolean); // Remove null values
      
      let currentPathIndex = 0;
      
      const tryNextPath = () => {
        if (currentPathIndex >= possiblePaths.length) {
          // All paths failed, proceed with WebSocket approach
          console.warn('⚠️ All library paths failed, using WebSocket approach');
          libraryLoadedRef.current = true;
          resolve();
          return;
        }
        
        script.src = possiblePaths[currentPathIndex];
        console.log(`📡 Trying library path: ${script.src}`);
        
        script.onload = () => {
          libraryLoadedRef.current = true;
          console.log(`✅ Pixel Streaming library loaded from: ${script.src}`);
          resolve();
        };
        
        script.onerror = () => {
          console.warn(`⚠️ Library path failed: ${script.src}`);
          currentPathIndex++;
          tryNextPath();
        };
      };
      
      // Remove old script if exists
      const oldScript = document.querySelector(`script[src*="${streamUrl}"]`);
      if (oldScript) {
        oldScript.remove();
      }
      
      tryNextPath();
      document.head.appendChild(script);
    });
  }, [streamUrl]);

  /**
   * Initialize the Pixel Streaming connection
   * Supports both standard Pixel Streaming library and direct WebSocket connection
   */
  const initializeStreaming = useCallback(async () => {
    if (!streamUrl) {
      setError('Stream URL is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Load the library first
      await loadPixelStreamingLibrary();

      // Create video element if it doesn't exist
      if (!videoElementRef.current) {
        const video = document.createElement('video');
        video.autoplay = true;
        video.playsInline = true;
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'contain';
        videoElementRef.current = video;
      }

      // Try to use Pixel Streaming library if available
      if (window.PixelStreaming) {
        try {
          const pixelStreaming = new window.PixelStreaming({
            initialSettings: {
              AutoConnect: true,
              StartVideoMuted: false,
              HoveringMouseMode: false,
              StreamerURL: streamUrl
            }
          });

          pixelStreamingRef.current = pixelStreaming;
          setPlayer(pixelStreaming);

          // Set up event listeners
          if (pixelStreaming.addEventListener) {
            pixelStreaming.addEventListener('connectionController', (event) => {
              if (event.detail) {
                const connectionController = event.detail;
                
                connectionController.addEventListener('open', () => {
                  console.log('✅ Pixel Streaming connected');
                  setIsConnected(true);
                  setIsLoading(false);
                });

                connectionController.addEventListener('close', () => {
                  console.log('⚠️ Pixel Streaming disconnected');
                  setIsConnected(false);
                });

                connectionController.addEventListener('error', (errorEvent) => {
                  console.error('❌ Pixel Streaming error:', errorEvent);
                  setError(errorEvent.detail || 'Connection error');
                  setIsLoading(false);
                });
              }
            });
          }

          // Set up video element
          if (pixelStreaming.videoElement) {
            videoElementRef.current.srcObject = pixelStreaming.videoElement.srcObject;
          }

          setIsLoading(false);
          return;
        } catch (libError) {
          console.warn('⚠️ Pixel Streaming library initialization failed, using WebSocket fallback:', libError);
        }
      }

      // Fallback: Direct WebSocket connection for command sending
      // Note: Video streaming typically requires the full Pixel Streaming setup
      // This is a simplified approach for command sending
      console.log('📡 Using WebSocket connection for commands');
      console.log('⚠️ Pixel Streaming library not available - video streaming may not work');
      console.log('📡 Attempting WebSocket connection to:', streamUrl);
      
      // Try to establish WebSocket connection for video streaming
      let wsUrl = streamUrl.replace(/^http/, 'ws');
      if (!wsUrl.endsWith('/')) {
        wsUrl += '/';
      }
      
      try {
        const ws = new WebSocket(wsUrl);
        wsConnectionRef.current = ws;
        
        ws.onopen = () => {
          console.log('✅ WebSocket connected for commands');
          setIsConnected(true);
          setIsLoading(false);
        };
        
        ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          setError('WebSocket connection failed. Video streaming requires Pixel Streaming library.');
          setIsLoading(false);
        };
        
        ws.onclose = (event) => {
          console.log('⚠️ WebSocket closed:', event.code, event.reason);
          setIsConnected(false);
        };
      } catch (wsError) {
        console.error('❌ Failed to create WebSocket:', wsError);
        setError('Failed to establish connection. Check server URL and network.');
        setIsLoading(false);
      }

    } catch (err) {
      console.error('❌ Error initializing Pixel Streaming:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [streamUrl, loadPixelStreamingLibrary]);

  /**
   * Send a command to the Unreal Engine instance
   * @param {string} command - The command name
   * @param {Object} args - Command arguments
   */
  const sendCommand = useCallback((command, args = {}) => {
    if (!isConnected) {
      console.warn('⚠️ Cannot send command: not connected to Pixel Streaming');
      return false;
    }

    // Track command timing for performance monitoring
    const commandId = `${command}_${Date.now()}_${Math.random()}`;
    const startTime = performance.now();
    commandTimingsRef.current.set(commandId, startTime);

    try {
      // Try Pixel Streaming library methods first
      if (pixelStreamingRef.current) {
        if (pixelStreamingRef.current.emitCommand) {
          pixelStreamingRef.current.emitCommand(command, args);
          const latency = performance.now() - startTime;
        console.log(`📤 Sent command via library: ${command} (${Math.round(latency)}ms)`, args);
        commandTimingsRef.current.delete(commandId);
        return true;
        }
        
        if (pixelStreamingRef.current.emitUIInteraction) {
          pixelStreamingRef.current.emitUIInteraction(command, args);
          const latency = performance.now() - startTime;
        console.log(`📤 Sent command via UI interaction: ${command} (${Math.round(latency)}ms)`, args);
        commandTimingsRef.current.delete(commandId);
        return true;
        }

        // Try response channel
        if (pixelStreamingRef.current.emit) {
          const message = {
            type: 'command',
            command: command,
            args: args
          };
          pixelStreamingRef.current.emit('command', message);
          const latency = performance.now() - startTime;
        console.log(`📤 Sent command via emit: ${command} (${Math.round(latency)}ms)`, args);
        commandTimingsRef.current.delete(commandId);
        return true;
        }
      }

      // Fallback: Direct WebSocket connection
      // Construct WebSocket URL from stream URL
      // For ALB path-based routing, preserve the path (e.g., /dev/)
      // Ensure trailing slash for WebSocket connection
      let wsUrl = streamUrl.replace(/^http/, 'ws');
      if (!wsUrl.endsWith('/')) {
        wsUrl += '/';
      }
      
      if (!wsConnectionRef.current || wsConnectionRef.current.readyState !== WebSocket.OPEN) {
        // Create WebSocket connection for commands
        try {
          const ws = new WebSocket(wsUrl);
          wsConnectionRef.current = ws;
          
          ws.onopen = () => {
            console.log('✅ WebSocket connected for commands');
          };
          
          ws.onerror = (error) => {
            console.error('❌ WebSocket error:', error);
          };
        } catch (wsError) {
          console.error('❌ Failed to create WebSocket:', wsError);
          return false;
        }
      }

      // Send command via WebSocket
      if (wsConnectionRef.current && wsConnectionRef.current.readyState === WebSocket.OPEN) {
        const message = JSON.stringify({
          type: 'command',
          command: command,
          args: args
        });
        wsConnectionRef.current.send(message);
        const latency = performance.now() - startTime;
        console.log(`📤 Sent command via WebSocket: ${command} (${Math.round(latency)}ms)`, args);
        commandTimingsRef.current.delete(commandId);
        return true;
      } else {
        // Use HTTP POST as final fallback
        // For ALB path-based routing, ensure path is preserved
        const apiUrl = streamUrl.endsWith('/') 
          ? `${streamUrl}api/command` 
          : `${streamUrl}/api/command`;
        fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ command, args })
        }).catch(err => {
          console.error('❌ Failed to send command via HTTP:', err);
        });
        const latency = performance.now() - startTime;
        console.log(`📤 Sent command via HTTP: ${command} (${Math.round(latency)}ms)`, args);
        commandTimingsRef.current.delete(commandId);
        return true;
      }
    } catch (err) {
      console.error('❌ Error sending command:', err);
      return false;
    }
  }, [isConnected, streamUrl]);

  /**
   * Send UI interaction (button click, etc.)
   * @param {string} elementId - The ID of the UI element in Unreal
   * @param {string} action - The action to perform (e.g., 'click', 'setValue')
   * @param {*} value - Optional value for the action
   */
  const sendUIInteraction = useCallback((elementId, action = 'click', value = null) => {
    if (!pixelStreamingRef.current || !isConnected) {
      console.warn('⚠️ Cannot send UI interaction: not connected');
      return false;
    }

    try {
      const interaction = {
        type: 'ui',
        elementId: elementId,
        action: action,
        value: value
      };

      if (pixelStreamingRef.current.emitUIInteraction) {
        pixelStreamingRef.current.emitUIInteraction(interaction);
      } else {
        sendCommand('uiInteraction', interaction);
      }

      console.log(`📤 Sent UI interaction: ${elementId}.${action}`, value);
      return true;
    } catch (err) {
      console.error('❌ Error sending UI interaction:', err);
      return false;
    }
  }, [isConnected, sendCommand]);

  /**
   * Disconnect from Pixel Streaming
   */
  const disconnect = useCallback(() => {
    if (pixelStreamingRef.current) {
      try {
        if (typeof pixelStreamingRef.current.close === 'function') {
          pixelStreamingRef.current.close();
        }
      } catch (err) {
        console.error('Error disconnecting Pixel Streaming:', err);
      }
      pixelStreamingRef.current = null;
    }
    
    if (wsConnectionRef.current) {
      try {
        wsConnectionRef.current.close();
      } catch (err) {
        console.error('Error closing WebSocket:', err);
      }
      wsConnectionRef.current = null;
    }
    
    setPlayer(null);
    setIsConnected(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isLoading,
    error,
    player,
    videoElementRef,
    initializeStreaming,
    sendCommand,
    sendUIInteraction,
    disconnect
  };
};

